import { MessageSquare, FileText, type LucideIcon } from 'lucide-react'
import i18n from '@claritio/frontend/lib/i18n'

// ENG-133 — the respondent's module list. Deliberately a single "Ocena" item at
// this stage (per-user-per-module grants are ENG-48/81, not built); it's a list so
// a granted "Raport" is a one-line add with no restructuring. Labels resolve lazily
// via the interview namespace so they honour the active locale.
export interface RespondentNavCtx {
  welcomeUrl: string
  chatUrl: string
  doneUrl: string
  status?: string
  // Where the respondent currently is — the race-proof fallback before status
  // has loaded, so clicking Ocena stays put instead of bouncing through welcome.
  currentPath?: string
  // ENG-73: true when the signed-in user's membership grants a report view
  // (employee, non-personal). Magic-link visitors have no account → false.
  reportAccess?: boolean
}

export interface RespondentModule {
  key: string
  labelKey: string
  icon: LucideIcon
  to?: (ctx: RespondentNavCtx) => string
  // Disabled state may depend on who's looking (ENG-73: Raport is live only for
  // users granted report access). Renders as a greyed row with a "soon" pill.
  soon?: boolean | ((ctx: RespondentNavCtx) => boolean)
}

export const RESPONDENT_MODULES: RespondentModule[] = [
  {
    key: 'ocena',
    labelKey: 'shell.nav.ocena',
    icon: MessageSquare,
    // Point straight at the current screen so clicking Ocena mid-interview lands
    // on the chat/done, not the welcome hero that would just redirect back. Until
    // status resolves, stay on the current path so a fast click never flashes.
    to: ({ welcomeUrl, chatUrl, doneUrl, status, currentPath }) =>
      status === 'completed' ? doneUrl
      : status === 'started' ? chatUrl
      : status === 'pending' ? welcomeUrl
      : (currentPath ?? welcomeUrl),
  },
  // Live for stakeholders granted report access (ENG-73); a greyed "soon" row
  // for everyone else, exactly as before.
  { key: 'raport', labelKey: 'shell.nav.raport', icon: FileText, to: () => '/report', soon: (ctx) => !ctx.reportAccess },
]

export const respondentModuleSoon = (m: RespondentModule, ctx: RespondentNavCtx): boolean =>
  typeof m.soon === 'function' ? m.soon(ctx) : !!m.soon

export const respondentModuleLabel = (m: RespondentModule) => i18n.t(`interview:${m.labelKey}`)

// Route-aware collapse default: tucked away on the chat screen, expanded on
// welcome/done. Pure so it's unit-testable; a manual toggle overrides it in the shell.
export function respondentSidebarCollapsedDefault(pathname: string): boolean {
  return pathname.endsWith('/chat')
}

// The footer adapts to who's in: an account respondent gets avatar + settings +
// log out; a magic-link visitor (no account) gets language + Powered-by only.
export type RespondentFooterVariant = 'account' | 'anonymous'
export function respondentFooterVariant(hasAccount: boolean): RespondentFooterVariant {
  return hasAccount ? 'account' : 'anonymous'
}
